import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import {
  CommunicationChannel,
  CommunicationDTO,
  CommunicationService,
  CommunicationStatus
} from '../../services/communication.service';
import { apiFailureMessage, httpErrorApiMessage } from '../../core/api-response-utils';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';

interface BitacoraChatGroup {
  dateKey: string;
  title: string;
  items: CommunicationDTO[];
}

@Component({
  selector: 'app-sale-communication-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
    '[class.h-full]': 'embedded()',
    '[class.min-h-0]': 'embedded()',
    '[class.flex]': 'embedded()',
    '[class.flex-col]': 'embedded()',
    '[class.flex-1]': 'embedded()'
  },
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TagModule,
    DrawerModule,
    TextareaModule,
    TooltipModule,
    PERMISSION_UI_DIRECTIVES
  ],
  templateUrl: './sale-communication-panel.component.html',
  styles: [
    `
      /* appendTo="body" saca el drawer del host: no usar :host ::ng-deep */
      ::ng-deep .communication-preview {
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        overflow: visible;
        overflow-wrap: anywhere;
        word-break: break-word;
        white-space: pre-wrap;
        line-height: 1.5;
      }
      ::ng-deep .communication-preview *,
      ::ng-deep .communication-preview ::before,
      ::ng-deep .communication-preview ::after {
        box-sizing: border-box;
        max-width: 100% !important;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      ::ng-deep .communication-preview p,
      ::ng-deep .communication-preview li,
      ::ng-deep .communication-preview span,
      ::ng-deep .communication-preview a,
      ::ng-deep .communication-preview div {
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      ::ng-deep .communication-preview pre,
      ::ng-deep .communication-preview code {
        white-space: pre-wrap !important;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      ::ng-deep .communication-preview table {
        width: 100% !important;
        max-width: 100% !important;
        table-layout: fixed;
        height: auto;
      }
      ::ng-deep .communication-preview td,
      ::ng-deep .communication-preview th {
        overflow-wrap: anywhere;
        word-break: break-word;
        white-space: normal !important;
      }
      ::ng-deep .communication-preview img {
        height: auto;
      }
      ::ng-deep .communication-preview p {
        margin: 0;
        padding: 0;
        min-height: 1.5em;
      }
      ::ng-deep .communication-preview ul,
      ::ng-deep .communication-preview ol {
        margin: 0;
        padding: 0 0 0 1.5em;
      }
      ::ng-deep .communication-preview li {
        margin: 0;
        padding: 0;
      }
      ::ng-deep .communication-preview ul {
        list-style-type: disc;
      }
      ::ng-deep .communication-preview ol {
        list-style-type: decimal;
      }
      ::ng-deep .communication-preview li[data-list='bullet'] {
        list-style-type: disc;
      }
      ::ng-deep .communication-preview li[data-list='ordered'] {
        list-style-type: decimal;
      }
      ::ng-deep .communication-preview .ql-ui {
        display: none;
      }
      ::ng-deep .communication-preview .ql-align-center { text-align: center; }
      ::ng-deep .communication-preview .ql-align-right { text-align: right; }
      ::ng-deep .communication-preview .ql-align-justify { text-align: justify; }

      .bitacora-chat__shell {
        background:
          radial-gradient(circle at 12% 8%, rgb(224 242 254 / 0.55), transparent 42%),
          radial-gradient(circle at 88% 0%, rgb(236 253 245 / 0.45), transparent 36%),
          linear-gradient(180deg, rgb(241 245 249) 0%, rgb(226 232 240) 100%);
      }

      .bitacora-chat__scroll {
        overscroll-behavior: contain;
      }

      .bitacora-chat__row {
        display: flex;
        align-items: flex-end;
        gap: 0.55rem;
        cursor: pointer;
      }

      .bitacora-chat__row--email {
        justify-content: flex-start;
      }

      .bitacora-chat__row--internal {
        justify-content: flex-end;
        flex-direction: row-reverse;
      }

      .bitacora-avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        border-radius: 9999px;
        font-size: 0.625rem;
        font-weight: 700;
        letter-spacing: 0.02em;
        color: white;
        text-transform: uppercase;
        flex-shrink: 0;
        box-shadow: 0 1px 2px rgb(15 23 42 / 0.12);
      }

      .bitacora-avatar--lg {
        width: 2.75rem;
        height: 2.75rem;
        font-size: 0.8125rem;
      }

      .bitacora-avatar[data-tone='0'] { background: rgb(79 70 229); }
      .bitacora-avatar[data-tone='1'] { background: rgb(14 165 233); }
      .bitacora-avatar[data-tone='2'] { background: rgb(16 185 129); }
      .bitacora-avatar[data-tone='3'] { background: rgb(245 158 11); }
      .bitacora-avatar[data-tone='4'] { background: rgb(236 72 153); }
      .bitacora-avatar[data-tone='5'] { background: rgb(99 102 241); }
      .bitacora-avatar[data-tone='6'] { background: rgb(20 184 166); }
      .bitacora-avatar[data-tone='7'] { background: rgb(248 113 113); }

      .bitacora-bubble {
        max-width: min(100%, 28rem);
        padding: 0.55rem 0.75rem 0.45rem;
        border-radius: 1rem;
        box-shadow: 0 1px 2px rgb(15 23 42 / 0.08);
        transition: transform 0.12s ease, box-shadow 0.12s ease;
      }

      .bitacora-chat__row:hover .bitacora-bubble {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgb(15 23 42 / 0.08);
      }

      .bitacora-bubble[data-channel='email'] {
        background: rgb(255 255 255);
        border: 1px solid rgb(226 232 240);
        border-bottom-left-radius: 0.35rem;
      }

      .bitacora-bubble[data-channel='internal'] {
        background: rgb(220 252 231);
        border: 1px solid rgb(187 247 208);
        border-bottom-right-radius: 0.35rem;
      }

      .bitacora-bubble[data-status='failed'][data-channel='email'] {
        background: rgb(254 242 242);
        border-color: rgb(254 202 202);
      }

      .bitacora-bubble__meta {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.35rem 0.5rem;
        margin-bottom: 0.25rem;
      }

      .bitacora-bubble__author {
        font-size: 0.75rem;
        font-weight: 700;
        color: rgb(15 23 42);
      }

      .bitacora-bubble__subject {
        margin: 0 0 0.2rem;
        font-size: 0.8125rem;
        font-weight: 600;
        color: rgb(30 41 59);
        word-break: break-word;
      }

      .bitacora-bubble__body {
        font-size: 0.8125rem;
        line-height: 1.4;
        color: rgb(51 65 85);
        word-break: break-word;
      }

      .bitacora-bubble__body p {
        margin: 0;
      }

      .bitacora-bubble__to {
        margin-top: 0.35rem !important;
        font-size: 0.6875rem;
        color: rgb(100 116 139);
      }

      .bitacora-bubble__footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.45rem;
        margin-top: 0.35rem;
      }

      .bitacora-bubble__time {
        font-size: 0.625rem;
        color: rgb(100 116 139);
        font-variant-numeric: tabular-nums;
      }

      .bitacora-bubble__status {
        display: inline-flex;
        align-items: center;
        gap: 0.2rem;
        font-size: 0.625rem;
        font-weight: 600;
      }

      .bitacora-bubble__status[data-status='sent'] { color: rgb(100 116 139); }
      .bitacora-bubble__status[data-status='opened'] { color: rgb(37 99 235); }
      .bitacora-bubble__status[data-status='failed'] { color: rgb(220 38 38); }
      .bitacora-bubble__status[data-status='pending'] { color: rgb(217 119 6); }
      .bitacora-bubble__status[data-status='unknown'] { color: rgb(100 116 139); }

      .bitacora-checks {
        display: inline-flex;
        align-items: center;
        position: relative;
        width: 0.85rem;
        height: 0.75rem;
      }

      .bitacora-checks .pi {
        font-size: 0.625rem;
        line-height: 1;
      }

      .bitacora-checks--read {
        width: 1.05rem;
      }

      .bitacora-checks--read .pi:first-child {
        position: absolute;
        left: 0;
      }

      .bitacora-checks--read .pi:last-child {
        position: absolute;
        left: 0.28rem;
      }

      .bitacora-channel-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0.1rem 0.45rem;
        border-radius: 9999px;
        font-size: 0.625rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        line-height: 1.2;
        white-space: nowrap;
      }

      .bitacora-channel-chip[data-channel='email'] {
        background: rgb(238 242 255);
        color: rgb(67 56 202);
      }

      .bitacora-channel-chip[data-channel='internal'] {
        background: rgb(241 245 249);
        color: rgb(71 85 105);
      }

      .bitacora-composer__field {
        position: relative;
      }

      .bitacora-composer textarea {
        font-size: 0.875rem;
        max-height: 8rem;
        padding-right: 3.25rem !important;
      }

      .bitacora-composer__send-wrap {
        position: absolute;
        inset: 0 0.5rem 0 auto;
        display: flex;
        align-items: center;
        pointer-events: none;
      }

      .bitacora-composer__send-wrap > * {
        pointer-events: auto;
      }
    `
  ]
})
export class SaleCommunicationPanelComponent implements AfterViewChecked {
  chargeId = input.required<number>();
  saleId = input<number | null>(null);
  embedded = input(false);
  showHeaderActions = input(true);
  allowInternalNotes = input(true);

  private readonly communicationService = inject(CommunicationService);
  private readonly toastr = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly chatScroll = viewChild<ElementRef<HTMLElement>>('chatScroll');
  readonly pageSize = 20;

  communications = signal<CommunicationDTO[]>([]);
  loading = signal(false);
  loadingMore = signal(false);
  loaded = signal(false);
  totalItems = signal(0);
  page = signal(0);

  detailDrawerVisible = signal(false);
  selectedCommunication = signal<CommunicationDTO | null>(null);

  internalNoteDraft = signal('');
  savingInternal = signal(false);

  private pendingScrollToBottom = false;
  private pendingScrollRestore: { previousHeight: number; previousTop: number } | null = null;

  chatGroups = computed(() => this.buildChatGroups(this.communications()));
  hasMore = computed(() => {
    const total = this.totalItems();
    return total > 0 && this.communications().length < total;
  });

  constructor() {
    effect(() => {
      const chargeId = this.chargeId();
      if (chargeId) {
        this.page.set(0);
        this.loaded.set(false);
        this.internalNoteDraft.set('');
        this.load();
      }
    });
  }

  ngAfterViewChecked(): void {
    const el = this.chatScroll()?.nativeElement;
    if (!el) {
      return;
    }
    if (this.pendingScrollRestore) {
      const { previousHeight, previousTop } = this.pendingScrollRestore;
      el.scrollTop = el.scrollHeight - previousHeight + previousTop;
      this.pendingScrollRestore = null;
      return;
    }
    if (this.pendingScrollToBottom) {
      el.scrollTop = el.scrollHeight;
      this.pendingScrollToBottom = false;
    }
  }

  load(): void {
    this.loadPage(false);
  }

  onChatScroll(event: Event): void {
    if (!this.hasMore() || this.loadingMore() || this.loading()) {
      return;
    }
    const el = event.target as HTMLElement;
    if (el.scrollTop <= 80) {
      this.loadPage(true);
    }
  }

  private loadPage(appendOlder: boolean): void {
    const chargeId = this.chargeId();
    if (!chargeId) {
      this.communications.set([]);
      this.totalItems.set(0);
      return;
    }

    if (appendOlder) {
      if (this.loadingMore() || this.loading() || !this.hasMore()) {
        return;
      }
      this.loadingMore.set(true);
    } else {
      this.page.set(0);
      this.loading.set(true);
    }

    const nextPage = appendOlder ? this.page() + 1 : 0;
    const scrollEl = this.chatScroll()?.nativeElement;
    const restore = appendOlder && scrollEl
      ? { previousHeight: scrollEl.scrollHeight, previousTop: scrollEl.scrollTop }
      : null;

    this.communicationService.list(
      { chargeId },
      {
        page: nextPage,
        size: this.pageSize,
        sort: ['createdAt,desc']
      }
    ).pipe(
      finalize(() => {
        this.loading.set(false);
        this.loadingMore.set(false);
        this.loaded.set(true);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        if (response.ok) {
          const batch = [...(response.body ?? [])].reverse();
          if (appendOlder) {
            this.communications.update((prev) => this.mergeUnique([...batch, ...prev]));
            this.page.set(nextPage);
            this.pendingScrollRestore = restore;
          } else {
            this.communications.set(batch);
            this.page.set(0);
            this.pendingScrollToBottom = true;
          }
          if (response.page) {
            this.totalItems.set(response.page.totalItems ?? 0);
          }
        } else if (!appendOlder) {
          this.communications.set([]);
          this.totalItems.set(0);
          this.toastr.error(apiFailureMessage(response, 'Error al cargar comunicaciones'));
        }
      },
      error: (err) => {
        if (!appendOlder) {
          this.communications.set([]);
          this.totalItems.set(0);
        }
        this.toastr.error(httpErrorApiMessage(err, 'Error al cargar comunicaciones'));
      }
    });
  }

  onCommunicationSent(): void {
    this.page.set(0);
    this.loaded.set(false);
    this.load();
  }

  canSaveInternalNote(): boolean {
    return !!this.chargeId() && this.internalNoteDraft().trim().length > 0 && !this.savingInternal();
  }

  onComposerEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey) {
      return;
    }
    keyboardEvent.preventDefault();
    this.saveInternalNote();
  }

  saveInternalNote(): void {
    const chargeId = this.chargeId();
    const messageBody = this.internalNoteDraft().trim();
    if (!chargeId || !messageBody || this.savingInternal()) {
      return;
    }
    this.savingInternal.set(true);
    this.communicationService.createInternal({
      chargeId,
      saleId: this.saleId() ?? undefined,
      messageBody
    }).pipe(
      finalize(() => {
        this.savingInternal.set(false);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        if (response.ok) {
          this.internalNoteDraft.set('');
          this.toastr.success('Nota interna guardada');
          this.onCommunicationSent();
        } else {
          this.toastr.error(apiFailureMessage(response, 'No se pudo guardar la nota'));
        }
      },
      error: (err) => {
        this.toastr.error(httpErrorApiMessage(err, 'No se pudo guardar la nota'));
      }
    });
  }

  openDetail(communication: CommunicationDTO): void {
    this.selectedCommunication.set(communication);
    this.detailDrawerVisible.set(true);
  }

  onDetailDrawerVisibleChange(visible: boolean): void {
    this.detailDrawerVisible.set(visible);
    if (!visible) {
      this.selectedCommunication.set(null);
    }
  }

  isInternal(item: CommunicationDTO | null | undefined): boolean {
    return this.channelKey(item?.channel) === 'internal';
  }

  isOpened(item: CommunicationDTO | null | undefined): boolean {
    return !!item?.openedAt && item.status === 'SENT';
  }

  deliveryStatusKey(item: CommunicationDTO): string {
    if (this.isOpened(item)) {
      return 'opened';
    }
    return this.timelineStatusKey(item.status);
  }

  deliveryStatusLabel(item: CommunicationDTO | null | undefined): string {
    if (!item) {
      return '—';
    }
    if (this.isOpened(item)) {
      return 'Abierto';
    }
    return this.statusLabel(item.status);
  }

  channelKey(channel?: CommunicationChannel | null): 'email' | 'internal' {
    return channel === 'INTERNAL' ? 'internal' : 'email';
  }

  channelLabel(channel?: CommunicationChannel | null): string {
    return channel === 'INTERNAL' ? 'Interno' : 'Correo';
  }

  channelIcon(channel?: CommunicationChannel | null): string {
    return channel === 'INTERNAL' ? 'pi pi-lock' : 'pi pi-envelope';
  }

  authorLabel(item: CommunicationDTO | null | undefined): string {
    if (!item) {
      return 'Usuario';
    }
    return item.sentByUserName || item.sentByUserEmail || 'Usuario';
  }

  authorInitials(item: CommunicationDTO | null | undefined): string {
    const label = this.authorLabel(item).trim();
    if (!label) {
      return '?';
    }
    const parts = label.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }

  avatarTone(item: CommunicationDTO | null | undefined): number {
    const seed = item?.sentByUserId ?? this.authorLabel(item);
    const text = String(seed);
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = (hash * 31 + text.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % 8;
  }

  previewText(item: CommunicationDTO): string {
    const raw = item.messageBody?.trim() || '';
    if (!raw) {
      return item.subject || '—';
    }
    const plain = this.isInternal(item)
      ? raw
      : raw
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\s+/g, ' ')
        .trim();
    if (!plain) {
      return item.subject || '—';
    }
    return plain.length > 280 ? `${plain.slice(0, 277)}…` : plain;
  }

  attachmentSummary(item: CommunicationDTO): string {
    const files = item.attachments ?? [];
    if (files.length === 0) {
      return '';
    }
    const first = files[0]?.name ?? 'archivo';
    if (files.length === 1) {
      return first;
    }
    return `${first} +${files.length - 1}`;
  }

  formatFileSize(size?: number): string {
    if (size == null || !Number.isFinite(size)) {
      return '';
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }

  timelineStatusKey(status?: CommunicationStatus): string {
    switch (status) {
      case 'SENT':
        return 'sent';
      case 'FAILED':
        return 'failed';
      case 'PENDING':
        return 'pending';
      default:
        return 'unknown';
    }
  }

  timelineStatusIcon(status?: CommunicationStatus): string {
    switch (status) {
      case 'SENT':
        return 'pi pi-check';
      case 'FAILED':
        return 'pi pi-times';
      case 'PENDING':
        return 'pi pi-clock';
      default:
        return 'pi pi-envelope';
    }
  }

  communicationInstant(item: CommunicationDTO): string | null {
    return item.sentAt ?? item.createdAt ?? null;
  }

  statusLabel(status?: CommunicationStatus): string {
    switch (status) {
      case 'SENT':
        return 'Enviado';
      case 'FAILED':
        return 'Fallido';
      case 'PENDING':
        return 'Pendiente';
      default:
        return status ?? '—';
    }
  }

  statusSeverity(status?: CommunicationStatus): 'success' | 'danger' | 'warn' | 'secondary' {
    switch (status) {
      case 'SENT':
        return 'success';
      case 'FAILED':
        return 'danger';
      case 'PENDING':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  private mergeUnique(items: CommunicationDTO[]): CommunicationDTO[] {
    const seen = new Set<number | string>();
    const result: CommunicationDTO[] = [];
    for (const item of items) {
      const key = item.id ?? `${item.createdAt}-${item.subject}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      result.push(item);
    }
    return result;
  }

  private buildChatGroups(items: CommunicationDTO[]): BitacoraChatGroup[] {
    const grouped = new Map<string, CommunicationDTO[]>();

    for (const item of items) {
      const instant = this.communicationInstant(item);
      if (!instant) {
        continue;
      }
      const date = new Date(instant);
      if (Number.isNaN(date.getTime())) {
        continue;
      }
      const dateKey = this.dateKeyLocal(date);
      const bucket = grouped.get(dateKey);
      if (bucket) {
        bucket.push(item);
      } else {
        grouped.set(dateKey, [item]);
      }
    }

    return [...grouped.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateKey, groupItems]) => {
        const sortedItems = [...groupItems].sort((a, b) => {
          const aTime = new Date(this.communicationInstant(a) ?? 0).getTime();
          const bTime = new Date(this.communicationInstant(b) ?? 0).getTime();
          return aTime - bTime;
        });
        return {
          dateKey,
          title: this.formatDayTitle(this.parseDateKey(dateKey)),
          items: sortedItems
        };
      });
  }

  private dateKeyLocal(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseDateKey(dateKey: string): Date {
    const [year, month, day] = dateKey.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private formatDayTitle(date: Date): string {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (this.dateKeyLocal(date) === this.dateKeyLocal(today)) {
      return 'Hoy';
    }
    if (this.dateKeyLocal(date) === this.dateKeyLocal(yesterday)) {
      return 'Ayer';
    }
    const label = date.toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
}
